import {
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
} from '@mui/material';
import { useState } from 'react';
import styled from 'styled-components';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getDeviceTags } from '../../services/DevicesService';
import { disassociateTag } from '../../services/TagsService';
import { TagKnown } from '../../Pages/ManageTags/TagsTable';
import { ModalContent } from './ModalContent';

interface DisassociateDeviceTagPanelProps {
  tag: TagKnown;
  onClose: () => void;
}

const StyledStack = styled(Stack)`
  max-height: 320px;
  width: 100%;
  overflow: auto;
`;

export const DisassociateDeviceTagPanel = (
  props: DisassociateDeviceTagPanelProps,
) => {
  const [checked, setChecked] = useState(['']);
  const { tag, onClose } = props;

  const {
    data: getDeviceTagsData,
    isPending: getDeviceTagsLoading,
    error,
  } = useQuery({
    queryKey: ['disassociate-device-tags', tag.tagName],
    queryFn: () => getDeviceTags({ tagName: tag.tagName, limit: 150 }),
    enabled: !!tag.tagName,
  });

  const { mutateAsync: disassociateMutate, isPending: disassociateTagLoading } =
    useMutation({
      mutationFn: (serialNumber: string) =>
        disassociateTag({ tagName: tag.tagName, serialNumber }),
    });

  const deviceTags = (getDeviceTagsData?.results ?? []).filter(Boolean) as {
    tagName: string;
    serialNumber: string;
  }[];

  const handleToggle = (value: string | undefined | null) => () => {
    if (value) {
      const currentIndex = checked.indexOf(value);
      const newChecked = [...checked];
      if (currentIndex === -1) {
        newChecked.push(value);
      } else {
        newChecked.splice(currentIndex, 1);
      }
      setChecked(newChecked);
    }
  };

  const handleDissociateTag = async () => {
    const filteredChecked = checked.filter((c) => c !== '');
    if (tag.tagName) {
      await Promise.all(filteredChecked.map((sn) => disassociateMutate(sn)));
    }
    onClose();
  };

  return (
    <div>
      <ModalContent
        title="Dissociate Device(s) from Tag"
        loading={getDeviceTagsLoading}
        error={error}
        handleClose={onClose}
        handleSubmit={handleDissociateTag}
        buttonLabels={{
          loading: 'Disassociating Tag(s)...',
          create: 'Disassociate Tag(s)',
        }}
        submitDisabled={getDeviceTagsLoading || disassociateTagLoading}
      >
        <StyledStack>
          <List sx={{ width: '100%' }}>
            {deviceTags.map((value) => {
              const labelId = `checkbox-list-label-${value.serialNumber}`;
              return (
                <ListItem key={value.serialNumber} disablePadding>
                  <ListItemButton
                    role={undefined}
                    onClick={handleToggle(value.serialNumber)}
                    dense
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={checked.includes(value.serialNumber)}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{ 'aria-labelledby': labelId }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      id={labelId}
                      primary={`${value.serialNumber}`}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </StyledStack>
      </ModalContent>
    </div>
  );
};
